import React, { useState } from 'react';
import { Grid, Typography, Card, CardHeader, CardContent } from '@mui/material';

import { useApiGet, useApiSend } from '../../../utils/httpClient';
import {
  getLCLeadership,
  postLCLeadership,
  patchLCLeadership,
  deleteLCLeadership,
  replaceExistingLCLeadership,
} from '../../../endpoints/chapters';
import { roles } from '../../../utils/variables';
import PositionDisplay from '../../../Components/PositionDisplay';

const LCLeadership = ({ id, lcAccess }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState(null);
  const {
    data: leadershipData,
    isFetching,
    refetch,
  } = useApiGet(
    ['lc-leadership', id],
    getLCLeadership,
    {
      enabled: true,
      retry: 1,
    },
    { id }
  );

  const { mutate: updateCommittee } = useApiSend(
    patchLCLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: deleteCommittee } = useApiSend(
    deleteLCLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );
  const { mutate: postCommittee } = useApiSend(
    postLCLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: replaceCommittee } = useApiSend(
    replaceExistingLCLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const onSavePosition = (position) => {
    setIsSaving(true);
    setErrorSaving(null);
    if (position.action === 'newAppointment') {
      //Handle post
      if (!position.id) {
        //Handle new appointment
        postCommittee({
          formId: id,
          user_id: position.userId,
          start_date: position.startDate,
          end_date: null,
          position_id: position.positionId,
          committee_id: position.committeeId,
        });
      } else {
        replaceCommittee({
          formId: id,
          id: position.id,
          user_id: position.userId,
          start_date: position.startDate,
          end_date: null,
        });
      }
    } else if (position.action === 'editDates') {
      //Handle patch
      updateCommittee({
        formId: id,
        id: position.id,
        start_date: position.startDate,
        end_date: position.endDate,
      });
    } else if (position.action === 'removeRecord') {
      //Handle delete
      deleteCommittee({
        formId: id,
        id: position.id,
      });
    }
  };

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  if (!leadershipData) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  const canEdit = () => {
    if (lcAccess) {
      let userRole = localStorage.getItem('role');
      let rolesAllowed = [
        roles.Admin,
        roles.Support,
        roles.SPDOfficer,
        roles.LCOfficer,
      ];
      return rolesAllowed.includes(userRole);
    }
    return false;
  };

  return (
    <Grid container>
      <Grid item xs={12} md={6} padding>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="Current Officers" />
          <CardContent>
            <Grid container spacing={2}>
              {leadershipData.officers
                .sort((a, b) => a.order - b.order)
                ?.sort((a, b) => a.position_id - b.position_id)
                .map((user) => (
                  <PositionDisplay
                    id={user.id}
                    position={user.position}
                    positionId={user.position_id}
                    committeeId={user.committee_id}
                    name={user.name}
                    startDate={user.start_date}
                    endDate={user.end_date}
                    canEdit={canEdit()}
                    isSaving={isSaving}
                    errorSavingText={errorSaving}
                    onSave={onSavePosition}
                    userId={user.user_id}
                    pageFrom={'lc-leadership'}
                    key={user.id}
                    localChapterId={id}
                  />
                ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      {leadershipData.project_chairs.length > 0 && (
        <Grid item xs={12} md={6} padding>
          <Grid item container xs={12}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader title="Project Chairs" sx={{ p: 1 }} />
              <CardContent sx={{ p: 1 }}>
                <Grid container spacing={2}>
                  {leadershipData.project_chairs.map((user) => (
                    <PositionDisplay
                      id={user.id}
                      position={user.position}
                      positionId={user.position_id}
                      committeeId={user.committee_id}
                      name={user.name}
                      startDate={user.start_date}
                      endDate={user.end_date}
                      canEdit={canEdit()}
                      isSaving={isSaving}
                      errorSavingText={errorSaving}
                      onSave={onSavePosition}
                      userId={user.user_id}
                      pageFrom={'lc-leadership'}
                      key={user.id}
                      localChapterId={id}
                    />
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default LCLeadership;
